import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAppData, localId } from '@/lib/appStorage';
import { mergeAppFromBlueprint, deletePageBySlug } from '../blueprint';
import { composeBlueprint } from '@/lib/appBlueprintComposer';
import {
  getSubmission,
  submitAppForReview,
  type AppSubmission,
} from '@/lib/appValidation';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  at: string;
  meta?: { createdPages?: string[]; updatedPages?: string[]; removedPages?: string[]; blocks?: number };
}

interface ChatState {
  messages: ChatMessage[];
}

interface Props {
  appId: number;
  appName: string;
  onChanged: () => void;
}

const QUICK_PROMPTS = [
  'Crée une app de gestion des tâches avec priorités et échéances',
  'Ajoute une page qui gère les dépenses avec un budget mensuel',
  'Ajoute une page de réservation avec formulaire de contact',
  'Ajoute une page qui présente le catalogue des produits',
];

export default function AiChatPanel({ appId, appName, onChanged }: Props) {
  const storage = useAppData<ChatState>('ai_chat', { messages: [] }, { scope: 'app', scopeRef: String(appId) });
  const { data, setData, ready } = storage;

  const [input, setInput] = useState('');
  const [working, setWorking] = useState(false);
  const [pageTitles, setPageTitles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submission, setSubmission] = useState<AppSubmission | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  const loadContext = useCallback(async () => {
    try {
      const { data: pages } = await supabase
        .from('app_pages')
        .select('title')
        .eq('app_id', appId)
        .order('position', { ascending: true });
      setPageTitles((pages || []).map((page) => String(page.title)));
    } catch {
      setPageTitles([]);
    }
  }, [appId]);

  useEffect(() => {
    loadContext();
    getSubmission(appId).then(setSubmission).catch(() => setSubmission(null));
  }, [loadContext, appId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [data.messages.length, working]);

  const messages = useMemo(() => data.messages || [], [data.messages]);

  const push = (message: ChatMessage) => {
    setData((prev) => ({ messages: [...(prev.messages || []), message] }));
  };

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || working) return;
    setInput('');
    setError(null);
    push({ id: localId('msg'), role: 'user', content: text, at: new Date().toISOString() });
    setWorking(true);

    try {
      const composed = composeBlueprint(text, {
        appName,
        pageTitles,
        history: messages.slice(-8).map((message) => ({ role: message.role, content: message.content })),
      });

      const createdPages: string[] = [];
      const updatedPages: string[] = [];
      const removedPages: string[] = [];
      let blockCount = 0;

      if (composed.removePageSlugs.length > 0) {
        for (const slug of composed.removePageSlugs) {
          const deleted = await deletePageBySlug(appId, slug);
          if (deleted) removedPages.push(slug);
        }
      }

      if (composed.blueprint.pages.length > 0) {
        const result = await mergeAppFromBlueprint(appId, composed.blueprint);
        createdPages.push(...result.createdPages);
        updatedPages.push(...result.updatedPages);
        blockCount = result.blocks;
      }

      push({
        id: localId('msg'),
        role: 'assistant',
        content: composed.reply,
        at: new Date().toISOString(),
        meta: {
          createdPages,
          updatedPages,
          removedPages,
          blocks: blockCount,
        },
      });

      await loadContext();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La génération a échoué.");
    } finally {
      setWorking(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitAppForReview(appId, appName);
      setSubmission(result);
      setShowAudit(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La soumission a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = () => {
    if (!submission) return { label: 'Brouillon', tone: 'bg-background-100 text-foreground-600', icon: 'ri-edit-line' };
    if (submission.status === 'pending') return { label: 'En validation', tone: 'bg-accent-100 text-accent-900', icon: 'ri-time-line' };
    if (submission.status === 'approved') return { label: 'Validée et publiée', tone: 'bg-secondary-100 text-secondary-900', icon: 'ri-checkbox-circle-line' };
    return { label: 'Refusée', tone: 'bg-red-50 text-red-700', icon: 'ri-close-circle-line' };
  };

  const badge = statusBadge();
  const verdictTone = submission?.verdict === 'blocked'
    ? 'text-red-700'
    : submission?.verdict === 'review'
      ? 'text-accent-900'
      : 'text-secondary-900';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4 items-start">
      {/* Chat */}
      <div className="bg-background-50 border border-background-200/60 rounded-xl flex flex-col min-h-[560px]">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-background-200/60">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-800 flex items-center justify-center">
            <i className="ri-sparkling-2-line text-sm"></i>
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground-950">Constructeur conversationnel</h3>
            <p className="text-[11px] text-foreground-500">
              Décrivez ce que vous voulez, l'IA compose les pages et les blocs dans la structure Zifek.
            </p>
          </div>
          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${badge.tone}`}>
            <i className={badge.icon}></i>
            {badge.label}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 max-h-[440px]">
          {!ready ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-chat-3-line text-foreground-400"></i>
                <p className="text-xs font-semibold text-foreground-700">Par où commencer ?</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {QUICK_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => send(prompt)}
                    className="text-left px-3 py-2.5 rounded-lg border border-background-200/60 bg-background-50 text-[11px] text-foreground-700 hover:border-foreground-300 hover:bg-background-100/50 transition-colors cursor-pointer"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-foreground-400 mt-4 leading-relaxed">
                L'IA respecte le modèle Zifek : pages, blocs, formulaires, tables de données et réglages.
                Elle ne supprime rien sans que vous le demandiez explicitement.
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[88%] ${message.role === 'user' ? '' : 'w-full'}`}>
                  <div
                    className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                      message.role === 'user'
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100/70 text-foreground-800'
                    }`}
                  >
                    {message.content}
                  </div>
                  {message.meta && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {(message.meta.createdPages || []).map((page) => (
                        <span key={`c-${page}`} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium">
                          + page {page}
                        </span>
                      ))}
                      {(message.meta.updatedPages || []).map((page) => (
                        <span key={`u-${page}`} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                          page {page} complétée
                        </span>
                      ))}
                      {(message.meta.removedPages || []).map((page) => (
                        <span key={`r-${page}`} className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-medium">
                          − page {page}
                        </span>
                      ))}
                      {message.meta.blocks ? (
                        <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px]">
                          {message.meta.blocks} bloc(s) ajouté(s)
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {working && (
            <div className="flex justify-start">
              <div className="rounded-xl px-3.5 py-2.5 bg-background-100/70 text-foreground-500 text-sm flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Composition de la structure…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {error && (
          <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="cursor-pointer" title="Fermer">
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        <div className="border-t border-background-200/60 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={2}
              placeholder="Ex. : ajoute une page qui gère les réservations avec un formulaire et un tableau…"
              className="flex-1 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 resize-none"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || working}
              className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-40 flex-shrink-0"
              title="Envoyer"
            >
              <i className={working ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
            </button>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[10px] text-foreground-400">
              Les modifications sont enregistrées dans le brouillon, pas encore visibles du public.
            </p>
            <button
              onClick={submit}
              disabled={submitting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[10px] font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-50"
            >
              <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-shield-check-line'}></i>
              {submitting ? 'Analyse…' : 'Soumettre à validation'}
            </button>
          </div>
        </div>
      </div>

      {/* Panneau latéral */}
      <aside className="space-y-3">
        <div className="bg-background-50 border border-background-200/60 rounded-xl p-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">État de validation</h4>
          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full text-[11px] font-semibold w-fit ${badge.tone}`}>
            <i className={badge.icon}></i>
            {badge.label}
          </div>
          {submission ? (
            <div className="mt-3 space-y-1.5 text-[11px] text-foreground-600">
              <p>
                Score de sécurité : <span className={`font-bold ${verdictTone}`}>{submission.score}/100</span>
              </p>
              <p>
                Verdict :{' '}
                <span className={`font-semibold ${verdictTone}`}>
                  {submission.verdict === 'safe' ? 'Sûr' : submission.verdict === 'review' ? 'À vérifier' : 'Risque élevé'}
                </span>
              </p>
              <p className="text-foreground-400">Soumis le {new Date(submission.submittedAt).toLocaleString('fr-FR')}</p>
              {submission.reviewerNote && <p className="text-foreground-500">Note : {submission.reviewerNote}</p>}
              <button
                onClick={() => setShowAudit(true)}
                className="mt-1 text-[11px] font-medium text-primary-700 cursor-pointer whitespace-nowrap"
              >
                Voir le rapport d'analyse
              </button>
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-foreground-500 leading-relaxed">
              Soumettez l'app pour que le SuperAdmin vérifie la sécurité et le code avant publication.
            </p>
          )}
        </div>

        <div className="bg-background-50 border border-background-200/60 rounded-xl p-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">Pages actuelles</h4>
          {pageTitles.length === 0 ? (
            <p className="text-[11px] text-foreground-400">Aucune page pour l'instant.</p>
          ) : (
            <ul className="space-y-1">
              {pageTitles.map((title) => (
                <li key={title} className="flex items-center gap-1.5 text-[11px] text-foreground-700">
                  <i className="ri-file-text-line text-foreground-400"></i>
                  {title}
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={loadContext}
            className="mt-2 flex items-center gap-1 text-[10px] text-foreground-400 hover:text-foreground-700 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
        </div>

        <div className="bg-background-100/60 border border-background-200/50 rounded-xl p-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1.5">Ce que l'IA sait faire</h4>
          <ul className="space-y-1 text-[11px] text-foreground-600">
            <li><i className="ri-check-line text-accent-700 mr-1"></i>Créer des pages complètes</li>
            <li><i className="ri-check-line text-accent-700 mr-1"></i>Ajouter formulaires et tableaux</li>
            <li><i className="ri-check-line text-accent-700 mr-1"></i>Compléter une page existante</li>
            <li><i className="ri-check-line text-accent-700 mr-1"></i>Supprimer une page sur demande</li>
            <li><i className="ri-close-line text-red-500 mr-1"></i>Refuser les demandes malveillantes</li>
          </ul>
        </div>
      </aside>

      {/* Rapport d'analyse */}
      {showAudit && submission && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/45" onClick={() => setShowAudit(false)} aria-hidden="true" />
          <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-background-50 rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-base font-bold font-heading text-foreground-950">Rapport de sécurité</h3>
                <p className="text-[11px] text-foreground-500">
                  {submission.appName} · {submission.snapshot.pages.length} page(s) · {submission.snapshot.tables.length} table(s)
                </p>
              </div>
              <button
                onClick={() => setShowAudit(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
                title="Fermer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-lg bg-background-100/70 p-3 text-center">
                <p className={`text-lg font-bold ${verdictTone}`}>{submission.score}</p>
                <p className="text-[10px] text-foreground-500">Score</p>
              </div>
              <div className="rounded-lg bg-background-100/70 p-3 text-center">
                <p className={`text-lg font-bold ${verdictTone}`}>
                  {submission.verdict === 'safe' ? 'Sûr' : submission.verdict === 'review' ? 'À vérifier' : 'Bloqué'}
                </p>
                <p className="text-[10px] text-foreground-500">Verdict</p>
              </div>
              <div className="rounded-lg bg-background-100/70 p-3 text-center">
                <p className="text-lg font-bold text-foreground-800">{submission.findings.length}</p>
                <p className="text-[10px] text-foreground-500">Alerte(s)</p>
              </div>
            </div>

            {submission.findings.length === 0 ? (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-secondary-100/60 text-secondary-900 text-xs">
                <i className="ri-shield-check-line mt-0.5"></i>
                Aucun motif dangereux détecté dans les pages, formulaires, tables et réglages.
              </div>
            ) : (
              <ul className="space-y-2">
                {submission.findings.map((finding, index) => (
                  <li key={index} className="rounded-lg border border-background-200/60 p-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                          finding.severity === 'high'
                            ? 'bg-red-100 text-red-700'
                            : finding.severity === 'medium'
                              ? 'bg-accent-100 text-accent-900'
                              : 'bg-background-100 text-foreground-600'
                        }`}
                      >
                        {finding.severity}
                      </span>
                      <p className="text-xs font-semibold text-foreground-900">{finding.label}</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-1">{finding.detail}</p>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1.5">
                Tables à provisionner après validation
              </h4>
              <pre className="text-[10px] bg-background-100/70 rounded-lg p-3 max-h-40 overflow-auto text-foreground-600 whitespace-pre-wrap">
                {submission.provisionSql}
              </pre>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setShowAudit(false)}
                className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}